import React, { useEffect, useState } from 'react';
import { ActiveRun } from '../hooks/useBuildManager';

const BUILD_DURATION_S = 10 * 60; // 10 minutes

function calcRemaining(run: ActiveRun | undefined): number {
  if (!run) return BUILD_DURATION_S;
  const elapsed = Math.floor((Date.now() - new Date(run.created_at).getTime()) / 1000);
  return Math.min(BUILD_DURATION_S, Math.max(0, BUILD_DURATION_S - elapsed));
}

function useRunCountdown(run: ActiveRun | undefined): { remaining: number; pct: number } {
  const [remaining, setRemaining] = useState(() => calcRemaining(run));

  useEffect(() => {
    if (!run) return;
    const tick = () => setRemaining(calcRemaining(run));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [run]);

  const pct = Math.min(100, Math.max(0, Math.round((remaining / BUILD_DURATION_S) * 100)));
  return { remaining, pct };
}

function fmtTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── In-progress view ──────────────────────────────────────────────────────────
interface InProgressProps {
  run: ActiveRun;
  onClose: () => void;
}

const BuildInProgress: React.FC<InProgressProps> = ({ run, onClose }) => {
  const { remaining, pct } = useRunCountdown(run);

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        <p className="text-sm font-medium text-gray-800">A build is currently in progress</p>
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Estimated time remaining</span>
          <span className="tabular-nums font-mono">{fmtTime(remaining)}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className="bg-amber-400 h-2 rounded-full transition-all duration-1000"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <a
        href={run.html_url}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-purple-600 hover:underline block mb-5"
      >
        View run on GitHub Actions ↗
      </a>

      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded text-sm transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ── Normal trigger view ───────────────────────────────────────────────────────
interface Props {
  siteName: string;
  isTriggering: boolean;
  selectedTag: string;
  triggerError: string | null;
  activeRun: ActiveRun | undefined;
  onTagChange: (tag: string) => void;
  onStart: () => void;
  onClose: () => void;
}

const BuildModal: React.FC<Props> = ({
  siteName,
  isTriggering,
  selectedTag,
  triggerError,
  activeRun,
  onTagChange,
  onStart,
  onClose,
}) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1003]"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-lg w-[90%] max-w-md shadow-2xl"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex justify-between items-center py-5 px-5 border-b border-gray-200">
        <h3 className="text-xl text-gray-800 font-semibold m-0">
          {activeRun ? 'Build In Progress' : `Generate Build — ${siteName}`}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">
          &times;
        </button>
      </div>

      {activeRun ? (
        <BuildInProgress run={activeRun} onClose={onClose} />
      ) : (
        <div className="p-5">
          <p className="text-sm text-gray-600 mb-4">
            Dispatches a GitHub Actions build for{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">qureai/qure-widget</code>{' '}
            at the selected tag.
          </p>

          <label className="block text-sm font-medium text-gray-700 mb-1">Widget tag</label>
          <input
            type="text"
            value={selectedTag}
            onChange={(e) => onTagChange(e.target.value)}
            placeholder="electron-1.2.3"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-4"
          />

          {triggerError && (
            <p className="text-xs text-red-600 mb-3">{triggerError}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onStart}
              disabled={!selectedTag || isTriggering}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white py-2 px-4 rounded text-sm font-medium transition-colors"
            >
              {isTriggering ? 'Starting…' : 'Start Build'}
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default BuildModal;
